/**
 * Geometry for the "Your Lab Trends" carousel.
 *
 * Every input here is measured at runtime (see useTrendCardLayout). Nothing in
 * this module may hardcode a card width: assay display names are editable in
 * Super Admin, so any constant derived from today's names is wrong the moment
 * someone renames an assay.
 *
 * Kept DOM-free on purpose — jsdom has no layout engine, so this is the only
 * layer where the arithmetic can be unit-tested.
 */

/** Horizontal gap between cards. Mirrors `gap-3` on the carousel strip in LabTrends. */
export const GAP = 12;

/**
 * Everything in a TrendCard that is not value or name text. Each part mirrors a
 * class in TrendCard.tsx — change one there and you must change it here. A test
 * pins the total so the mirror cannot drift silently, which is exactly how the
 * old hardcoded 252px scroll step desynced from its 202px card.
 */
export const CARD_CHROME_PARTS = {
    border: 2, //          `border`, both sides (Tailwind is border-box)
    padding: 32, //        `px-4`, both sides
    arrowIcon: 16, //      `h-4 w-4`
    arrowGap: 2, //        `gap-0.5`
    valueNameGap: 12, //   `gap-3`
    namePadding: 16, //    `pr-4` — with px-4 this ends the name 32px from the card's right edge, 8px clear of the magnifier's left edge at 24px
} as const;

export const CARD_CHROME = Object.values(CARD_CHROME_PARTS).reduce((total, part) => total + part, 0);

export interface TrendLayoutInput {
    /** Measured width of the overflow-hidden carousel viewport. */
    containerWidth: number;
    /** Widest rendered value string, px. */
    maxValueWidth: number;
    /** Widest rendered name string on a single line, px. */
    maxNameWidth: number;
}

export interface TrendLayout {
    cardsPerView: number;
    cardWidth: number;
    /** cardWidth + GAP — the distance one arrow click travels. */
    slotWidth: number;
    /** Exposed for tests and debugging. */
    minCardWidth: number;
}

/**
 * Returns null until a real container measurement exists, which the caller uses
 * to suppress the pre-measure paint.
 */
export function computeTrendLayout({
    containerWidth,
    maxValueWidth,
    maxNameWidth,
}: TrendLayoutInput): TrendLayout | null {
    if (!Number.isFinite(containerWidth) || containerWidth <= 0) return null;

    const minCardWidth = CARD_CHROME + Math.max(0, maxValueWidth) + Math.max(0, maxNameWidth);

    // How many whole cards fit. The trailing card needs no gap after it, so add
    // one GAP to both sides of the division to cancel it out.
    const cardsPerView = Math.max(1, Math.floor((containerWidth + GAP) / (minCardWidth + GAP)));

    // Spend the leftover space so N cards fill the viewport exactly. Card N+1
    // then begins at containerWidth and is entirely hidden by overflow-hidden,
    // rather than being sliced by the edge.
    //
    // max() matters only when the container is narrower than a single card:
    // there the card keeps its content width and overflows, because never
    // clipping a name outranks fitting the viewport.
    const cardWidth = Math.max(minCardWidth, (containerWidth - (cardsPerView - 1) * GAP) / cardsPerView);

    return {cardsPerView, cardWidth, slotWidth: cardWidth + GAP, minCardWidth};
}
